import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ecco/ui/text";
import { Money } from "@ecco/ui/money";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { NextImage } from "@ecco/ui/next-image";
import { NextLink } from "@components/ui/next-link/NextLink";
import { Popover, PopoverBody, PopoverContent } from "@ecco/ui/popover";
import type { HotspotProductModel } from "@lib/contentful/mappers/modules/hotspot/types";
import type { FC } from "react";
import type { CustomMarkerProps } from "./CustomMarker";
import { MarkerTrigger } from "./MarkerTrigger";
import { styles } from "./styles";
import { calculateLeft, calculateTop, useMarkerPopover } from "./useMarkerPopover";
import { useLocale } from "next-intl";
import { getPDPSlug } from "@lib/utils/getPDPSlug";

type CustomMarkerProductProps = CustomMarkerProps & { product: HotspotProductModel };

export const CustomMarkerProduct: FC<CustomMarkerProductProps> = (
    props: CustomMarkerProductProps
) => {
    const { product, left, top } = props;
    const {
        name,
        articleNumber,
        colorCode,
        descriptiveHeading,
        image,
        priceRaw,
        listPriceRaw,
        discountPercentage,
    } = product;

    const { isOpen, onOpen, onClose, onToggle, placement, placementY, popoverProps } =
        useMarkerPopover(top, left);
    const { get: getMicrocopy } = useMicrocopy();
    const locale = useLocale();

    const viewProductMicrocopy =
        getMicrocopy(CommonCMS.shared, CommonCMS.ariaLabels.viewProductAria) || "View product";

    return (
        <Popover {...popoverProps}>
            <MarkerTrigger isOpen={isOpen} onOpen={onOpen} onToggle={onToggle} zIndex={2} />
            <NextLink
                href={getPDPSlug(name, articleNumber, colorCode, locale).slug}
                aria-label={`${viewProductMicrocopy} ${name}`}
                position="absolute"
                w="100%"
                h="100%"
                onMouseLeave={onClose}
                pointerEvents={isOpen ? "auto" : "none"}
                color="black"
                left={0}
                top={0}
            >
                <PopoverContent
                    rootProps={{ style: { transform: "scale(0)" } }}
                    sx={{ ...styles.popoverContent, minW: ["100%", "345px"] }}
                >
                    <PopoverBody p={3} zIndex={3}>
                        <Flex justifyContent="flex-start" flexDirection="row" gap={2} flexGrow={1}>
                            {image && (
                                <Box sx={styles.popoverProductImage} backgroundColor={"gray.100"}>
                                    <NextImage sizes={["20vw"]} src={image} alt="" />
                                </Box>
                            )}
                            <Flex
                                justifyContent={"space-between"}
                                flexDirection={"column"}
                                w="100%"
                            >
                                <Flex flexDirection={"column"} w="100%">
                                    <Text sx={styles.popoverProductName} textTransform="capitalize">
                                        {name}
                                    </Text>
                                    {descriptiveHeading && (
                                        <Text
                                            sx={{
                                                ...styles.popoverDescription,
                                                color: "gray.500",
                                            }}
                                        >
                                            {descriptiveHeading}
                                        </Text>
                                    )}
                                </Flex>
                                {discountPercentage ? (
                                    <Flex flexDirection={"column"}>
                                        <Flex gap={2} alignItems={"center"} mb={2}>
                                            <Money
                                                fontSize={[
                                                    "desktopBodyTextTiny",
                                                    "desktopBodyTextSmall",
                                                ]}
                                                fontWeight="bold"
                                                locale={locale}
                                                data-testid="hotspotprice"
                                                data={priceRaw}
                                            />
                                            <Money
                                                fontWeight="bold"
                                                fontSize={[
                                                    "desktopBodyTextTiny",
                                                    "desktopBodyTextSmall",
                                                ]}
                                                color="gray.500"
                                                textDecoration="line-through"
                                                locale={locale}
                                                data-testid="hotspotrecommendedprice"
                                                data={listPriceRaw}
                                            />
                                        </Flex>
                                    </Flex>
                                ) : (
                                    <Money
                                        data={listPriceRaw}
                                        locale={locale}
                                        convertFromCents={true}
                                        fontWeight="semibold"
                                        data-testid="hotspotprice"
                                        fontSize={["desktopBodyTextTiny", "desktopBodyTextSmall"]}
                                    />
                                )}
                            </Flex>
                            <Flex>
                                <ECCOIcon transform={"rotate(-90deg)"} name="chevron" />
                            </Flex>
                        </Flex>
                    </PopoverBody>
                    <Box
                        onMouseLeave={onClose}
                        display={["none", "block"]}
                        position={"absolute"}
                        height="150%"
                        width="100%"
                        top={calculateTop(placementY)}
                        left={calculateLeft(placement)}
                    ></Box>
                </PopoverContent>
            </NextLink>
        </Popover>
    );
};
